import React, { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { media } from 'styled-bootstrap-grid';
import styled, { css } from 'styled-components';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Property } from 'shared-types/Property';
import { BasketDetails } from 'shared-types/Basket';
import { Product as ProductType } from 'shared-types/Product';

import { ImagePreview } from '@common/components/ImagePreview';
import Markdown from '@common/components/Markdown';
import { PostageOptions } from '@common/components/PostageOptions';
import SectionContainer from '@common/components/SectionContainer';
import Suggestions from '@common/components/Suggestions';
import { Heading4 } from '@common/components/Typography';

import { addItemToBasket } from '@utils/basket/addToBasket';
import { getMainCategoryId, mergeCrossSellWithUpsell } from '@utils/helpers';

import { createBasket, updateBasket } from '@store/basket/actions';

import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';

import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useBasketDrawerCheckoutCta } from '@common/components/BasketDrawer/hooks/useBasketDrawerCheckoutCta';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import { DiscountBanner } from '@common/components/Price';
import { ItemDetailsDescription } from '@features/AddedToBasket/Retail/ItemDetailsDescription';
import { spaces } from '@common/theme/spaces';
import { useTranslations } from '@lang';
import { isMobile } from '@utils/grid';
import { useGetSpaSettings } from '@features/spa';

import {
  AddToBasketFormSchema,
  AddToBasketFormValues,
} from '@features/AddToBasketForm/AddToBasketForm.types';

const ProductWrapper = styled(SectionContainer)`
  padding-top: 20px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.md`
    flex-direction: row;
    align-items: flex-start;
    padding-top: 80px;
    padding-bottom: 80px;
  `}
`;

const ProductWrapperColumn = styled.div<{ sticky?: boolean }>`
  width: 100%;

  ${media.md`
    width: 50%;
  `}

  ${({ sticky }) =>
    sticky &&
    css`
      margin-right: -20px;
      margin-left: -20px;
      width: calc(100% + 40px);

      ${media.md`
        margin-right: 0;
        margin-left: 0;
        position: sticky;
        top: 20px;
      `}
    `}
`;

const DescriptionWrapper = styled(SectionContainer)`
  max-width: 760px;
  padding: 20px 0;
  line-height: 150%;

  ${media.md`
    padding: 40px 0 80px 60px;
  `}
`;

const Title = styled(Heading4)`
  margin-top: 0;
  margin-bottom: 25px;

  ${media.md`
    padding-left: 60px;
  `}
`;

type ProductProps = {
  id: string;
  language: string;
};

type AppState = { basket: { basketDetails: BasketDetails } } & Record<string, unknown>;

type ProductEntity = ProductType & {
  upSell?: ProductType[];
  crossSellObjects?: ProductType[];
  description?: string;
};

const Product: React.FC<ProductProps> = ({ id, language }) => {
  const { t } = useTranslations();
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, AnyAction>>();

  const property = useSelector(entitySelectors.getProperty) as Property;
  const product = useSelector((state) => entitySelectors.getProduct(state, id)) as ProductEntity;
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as BasketDetails;

  const modules = Object.keys(property.modulesExcludingDisabled);
  const checkoutCta = useBasketDrawerCheckoutCta();

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: basketDetails?.items ?? [],
  });
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const singleOption = product.options?.length === 1 ? product.options[0] : null;

  const form = useForm<AddToBasketFormValues>({
    resolver: zodResolver(AddToBasketFormSchema),
    defaultValues: {
      option: singleOption,
      quantity: 1,
      personalization: null,
      product: { id },
    },
  });

  const currentOption = form.watch('option');

  const [isBasketSucceeded, setIsBasketSucceeded] = useState(false);

  const handleBasketUpdate = useCallback(
    (data: AddToBasketFormValues) => {
      addItemToBasket({
        type: 'products',
        data,
        createBasket: (propertyId, lang, details) =>
          dispatch(createBasket(propertyId as number, lang, details)),
        updateBasket: (propertyId, basketId, lang, details) =>
          dispatch(updateBasket(propertyId as number, basketId, lang, details)),
        property,
        basketDetails,
        language,
        onSuccess: () => {
          setIsBasketSucceeded(true);
        },
      });
    },
    [dispatch, property, basketDetails, language]
  );

  const mergedItems = mergeCrossSellWithUpsell(product.upSell, 'product', product.crossSellObjects);

  return (
    <FormProvider {...form}>
      {isBasketSucceeded && (
        <BasketDrawer
          currencyCode={basketDetails.total.currencyCode}
          numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
          open={isBasketSucceeded}
          onClose={() => setIsBasketSucceeded(false)}
          title={t('basketDrawer.title')}
          totalCost={basketDetails.total.amount}
          items={basketDetails?.items}
          modules={modulesToRecommend}
          hideArrivalTimes={hideArrivalTimes}
          {...checkoutCta}
        />
      )}
      {product.purchaseLimit !== 0 && <div id="options-button" />}
      <ProductWrapper>
        {isMobile() && <Title>{product.name}</Title>}
        <ProductWrapperColumn sticky>
          <DiscountBanner discount={product.discount} margin={spaces.space5} />
          <ImagePreview images={product.imageGallery ?? []} altLabel={product.name} />
        </ProductWrapperColumn>
        <ProductWrapperColumn>
          {!isMobile() && <Title>{product.name}</Title>}
          <ItemDetailsDescription
            item={product}
            handleBasketUpdate={handleBasketUpdate}
            currentOption={currentOption}
            purchaseLimit={product.purchaseLimit ?? null}
          />
          <PostageOptions property={property} />
          <DescriptionWrapper>
            <Markdown text={product.description} />
          </DescriptionWrapper>
        </ProductWrapperColumn>
      </ProductWrapper>
      <Suggestions items={mergedItems} leadingCategoryId={getMainCategoryId(product)} />
    </FormProvider>
  );
};

export default Product;
